import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";

// Parses YAML-ish frontmatter from a markdown string and returns { frontmatter, body }
export function parseFrontmatter(md) {
  if (!md) return { frontmatter: {}, body: "" };
  const m = md.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!m) return { frontmatter: {}, body: md };
  const fm = {};
  m[1].split("\n").forEach((line) => {
    const idx = line.indexOf(":");
    if (idx === -1) return;
    const k = line.slice(0, idx).trim();
    const v = line.slice(idx + 1).trim().replace(/^"|"$/g, "");
    if (k) fm[k] = v;
  });
  return { frontmatter: fm, body: m[2].trim() };
}

const components = {
  h1: ({ node, ...p }) => <h1 className="mt-4 mb-2 text-lg font-semibold tracking-[-0.005em] text-1" {...p} />,
  h2: ({ node, ...p }) => <h2 className="mt-5 mb-2 text-[13px] font-semibold uppercase tracking-[0.16em] text-[color:var(--accent-cyan)]" {...p} />,
  h3: ({ node, ...p }) => <h3 className="mt-4 mb-1.5 text-sm font-semibold text-1" {...p} />,
  p:  ({ node, ...p }) => <p  className="my-2 text-[13.5px] leading-[1.7] text-foreground/85" {...p} />,
  ul: ({ node, ...p }) => <ul className="my-2 list-disc pl-5 space-y-1 marker:text-[color:var(--accent-cyan)]" {...p} />,
  ol: ({ node, ...p }) => <ol className="my-2 list-decimal pl-5 space-y-1 marker:text-[color:var(--accent-cyan)]" {...p} />,
  li: ({ node, ...p }) => <li className="text-[13.5px] leading-[1.65] text-foreground/85" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-1" {...p} />,
  em: ({ node, ...p }) => <em className="text-[color:var(--accent-cyan)] not-italic font-medium" {...p} />,
  blockquote: ({ node, ...p }) => (
    <blockquote className="my-3 border-l-2 pl-3 italic text-foreground/75" style={{ borderColor: "rgba(143, 224, 74,0.45)" }} {...p} />
  ),
  code: ({ inline, className: cls, children, ...rest }) => {
    if (inline) {
      return <code className="rounded border px-1 py-0.5 font-mono text-[11.5px] text-[color:var(--accent-cyan)]" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }} {...rest}>{children}</code>;
    }
    return (
      <pre className="my-3 overflow-auto scrollbar-thin rounded-lg border p-3 font-mono text-[12px] leading-[1.65] text-foreground/85" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
        <code {...rest}>{children}</code>
      </pre>
    );
  },
  hr: () => <hr className="my-4 border-0 h-px" style={{ backgroundColor: "var(--border-subtle)" }} />,
  a: ({ node, ...p }) => <a className="text-[color:var(--accent-cyan)] underline-offset-2 hover:underline" {...p} />,
};

export function MarkdownView({ source, className }) {
  return (
    <div className={cn("markdown-view", className)} data-testid="markdown-view">
      <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
        {source || ""}
      </ReactMarkdown>
    </div>
  );
}
